import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { LogOut, Settings, SlidersHorizontal, User } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Separator,
  cn,
} from '@asad/crane-ui'

import { useAppDispatch, useAppSelector } from '@/hooks/reduxHooks'
import { doLogout } from '@/store/slices/auth-slice'

import { headerForPath } from '../pageHeaders'
import { Sidebar } from './Sidebar'

/** Nagłówek podstrony, zegar i menu użytkownika. */

const DATE_TIME_LOCALE: Record<string, string> = { pl: 'pl-PL', en: 'en-GB' }

const Topbar = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { t, i18n } = useTranslation()
  const { pathname } = useLocation()
  const profile = useAppSelector((state) => state.profile?.data)
  const [now, setNow] = React.useState(() => new Date())

  const header = headerForPath(pathname)
  const headerTitle = header ? t(header.titleKey) : null
  const headerDescription = header?.descriptionKey ? t(header.descriptionKey) : undefined

  React.useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000)

    return () => clearInterval(timer)
  }, [])

  const locale = DATE_TIME_LOCALE[i18n.language] ?? DATE_TIME_LOCALE.pl
  const dateFormat = React.useMemo(
    () => new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'long', year: 'numeric' }),
    [locale],
  )
  const timeFormat = React.useMemo(
    () => new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
    [locale],
  )

  const firstName = profile?.firstName ?? ''
  const lastName = profile?.lastName ?? ''
  const fullName = `${firstName} ${lastName}`.trim()
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
  const role = (profile?.role ?? []).map((userRole) => t(`roles.${userRole}`)).join(', ')

  return (
    <header className='crane-ui-surface sticky top-0 z-20 block border-b border-solid border-crane-border bg-crane-background py-1.5'>
      <div className='flex h-crane-topbar w-full min-w-0 items-center gap-2 px-3.5 md:gap-4 md:px-5'>
        <Sidebar mobile />
        <div className='min-w-0 flex-1 pr-3'>
          {header && (
            <>
              <h1 className='truncate text-crane-page-title font-semibold text-crane-foreground'>{headerTitle}</h1>
              {headerDescription && (
                <p className='mt-px truncate text-crane-page-subtitle text-crane-muted-foreground crane-lt-lg:hidden'>
                  {headerDescription}
                </p>
              )}
            </>
          )}
        </div>

        <div className='ml-auto flex shrink-0 items-center gap-2 md:gap-5'>
          <div className='flex items-baseline gap-crane-clock-gap whitespace-nowrap'>
            <span className='text-crane-clock-date text-crane-muted-foreground crane-lt-xl:hidden'>
              {dateFormat.format(now)}
            </span>
            <span className='text-crane-clock-time font-semibold tabular-nums text-crane-foreground'>
              {timeFormat.format(now)}
            </span>
          </div>

          <Separator orientation='vertical' className='hidden h-crane-topbar-divider self-center opacity-60 md:block' />

          <DropdownMenu>
            <DropdownMenuTrigger
              title={t('menu.userMenu')}
              className={cn(
                'flex h-crane-topbar items-center gap-2.5 rounded-crane-md py-0 pl-2 pr-1 transition-colors',
                'hover:bg-crane-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-crane-primary/40',
                'data-[state=open]:bg-crane-accent/60',
              )}
            >
              <div className='flex flex-col gap-0.5 text-right crane-lt-sm:hidden'>
                <span className='whitespace-nowrap text-crane-user-name font-semibold text-crane-foreground'>
                  {fullName}
                </span>
                <span className='whitespace-nowrap text-crane-user-role text-crane-muted-foreground crane-lt-md:hidden'>
                  {role}
                </span>
              </div>
              <span className='grid size-crane-avatar shrink-0 place-items-center rounded-crane-md bg-crane-primary/20 text-crane-avatar-initials font-bold text-crane-foreground'>
                {initials}
              </span>
            </DropdownMenuTrigger>

            <DropdownMenuContent align='end' className='min-w-crane-menu'>
              <div className='px-2.5 pb-2.5 pt-2'>
                <div className='text-crane-menu-name font-bold text-crane-popover-foreground'>{fullName}</div>
                <div className='text-crane-menu-role text-crane-muted-foreground'>{role}</div>
              </div>

              <DropdownMenuItem className='font-semibold' onSelect={() => navigate('/profile')}>
                <User aria-hidden />
                {t('menu.profile')}
              </DropdownMenuItem>
              <DropdownMenuItem className='font-semibold' onSelect={() => navigate('/profile/settings')}>
                <Settings aria-hidden />
                {t('menu.profileSettings')}
              </DropdownMenuItem>
              <DropdownMenuItem className='font-semibold' onSelect={() => navigate('/profile/settings?tab=appearance')}>
                <SlidersHorizontal aria-hidden />
                {t('menu.preferences')}
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              {/* Bez toastu i bez navigate: `doLogout` sam powiadamia i czyści store,
                  a przekierowanie robi RequireAuth. Dodanie ich tu daje podwójny komunikat. */}
              <DropdownMenuItem
                className='font-semibold text-crane-status-alarm focus:text-crane-status-alarm'
                onSelect={() => {
                  void dispatch(doLogout())
                }}
              >
                <LogOut aria-hidden />
                {t('menu.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  )
}

export { Topbar }
export default Topbar
